import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import FirstVisitMonitorLink from "@/components/feature/FirstVisitMonitorLink";
import { ReserveButton } from "@/components/layout/ReserveButton";
import { LineButton } from "@/components/layout/LineButton";
import { clinic } from "@/mocks/clinic";
import { ConcernList, FeatureList, TreatmentIndexNav } from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentPriceLink from "@/pages/treatment/components/TreatmentPriceLink";
import InjectablesSubHero from "./InjectablesSubHero";
import type { InjectablesAreaGroup, InjectablesDetail } from "@/mocks/injectablesPages";
import { isPendingValue } from "@/utils/isPendingContent";

/**
 * 注入治療カテゴリ（BOTOX / ヒアルロン酸）共通の正式コンテンツページ。
 * NOTE: 効果断定表現・未確認の医療情報（製剤名・単位数・注入量・持続期間など）は掲載しない。
 *       未確定の項目は「—」として、確定後に追記できる構造にしている。
 */

type SectionTone = "plain" | "taupe";

function Section({
  id,
  en,
  jp,
  lead,
  tone = "plain",
  children,
}: {
  id: string;
  en: string;
  jp: string;
  lead?: string;
  tone?: SectionTone;
  children: ReactNode;
}) {
  const toneClass = tone === "taupe" ? "bg-secondary-50" : "bg-background-50";
  return (
    <section id={id} className={`${toneClass} py-16 md:py-20 lg:py-24`}>
      <div className="u-container">
        <Reveal>
          <div className="flex flex-col gap-5 md:max-w-2xl">
            <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{en}</span>
            <span className="block h-px w-10 bg-accent-500" />
            <h2 className="font-heading text-[24px] leading-[1.55] tracking-[0.01em] text-foreground-950 md:text-[32px]">
              {jp}
            </h2>
            {lead ? (
              <p className="whitespace-pre-line text-[13px] leading-[2.1] text-foreground-700 md:text-sm">{lead}</p>
            ) : null}
          </div>
        </Reveal>
        <div className="mt-10 md:mt-14">{children}</div>
      </div>
    </section>
  );
}

/** 施術部位。PCでは顔／身体の2グループ、スマホでは縦並び。過度に大きなカードにしない。 */
function AreaGroups({ groups }: { groups: InjectablesAreaGroup[] }) {
  const isMulti = groups.length > 1;
  return (
    <div className={isMulti ? "grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12" : ""}>
      {groups.map((group, index) => (
        <div key={group.jp || `group-${index}`}>
          {group.jp ? (
            <div className="flex items-baseline gap-4">
              {group.en ? (
                <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{group.en}</span>
              ) : null}
              <span className="font-heading text-lg text-foreground-950">{group.jp}</span>
              <span className="h-px flex-1 bg-foreground-950/10" />
            </div>
          ) : null}
          <ul
            className={`grid grid-cols-2 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-3 ${
              isMulti ? "" : "lg:grid-cols-5"
            } ${group.jp ? "mt-6" : ""}`}
          >
            {group.items.map((item) => (
              <li
                key={item}
                className="flex items-center justify-center bg-background-50 px-4 py-5 text-center font-heading text-[14px] text-foreground-900 transition-colors hover:bg-secondary-100/60 md:text-[15px]"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** ページ下部の控えめな関連リンク。大きなカードは使わず、細い罫線＋テキストリンク＋小さな矢印。 */
function RelatedLinks({ items }: { items: { label: string; to: string }[] }) {
  return (
    <section className="border-t border-background-300/70 bg-background-50 py-16 md:py-20 lg:py-24">
      <div className="u-container">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.8fr] md:gap-16 lg:gap-24">
          <Reveal>
            <div className="flex flex-col">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">RELATED LINKS</span>
              <span className="mt-5 block h-px w-10 bg-accent-500" />
              <h2 className="mt-6 font-heading text-[20px] leading-[1.6] text-foreground-950 md:text-[24px]">
                関連ページ
              </h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <ul className="border-t border-foreground-950/12">
              {items.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="group flex items-center justify-between gap-4 border-b border-foreground-950/12 py-6 transition-colors"
                  >
                    <span className="font-heading text-[15px] text-foreground-900 transition-colors group-hover:text-accent-700 md:text-base">
                      {link.label}
                    </span>
                    <i
                      className="ri-arrow-right-line text-base text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-700"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function InjectablesSubDetail({ data }: { data: InjectablesDetail }) {
  // ダウンタイムがすべて未確定（「—」）の場合はセクションごと非表示にする
  const downtimeRows = data.downtime.filter((item) => !isPendingValue(item.value));
  const indexItems = [
    { label: "この施術について", target: "sp-about" },
    { label: "施術部位", target: "sp-areas" },
    { label: "このようなお悩みの方へ", target: "sp-recommended" },
    { label: "特徴", target: "sp-features" },
    { label: "施術情報", target: "sp-method" },
    { label: "施術の流れ", target: "sp-flow" },
    ...(downtimeRows.length > 0 ? [{ label: "ダウンタイム", target: "sp-downtime" }] : []),
    { label: "リスク・注意事項", target: "sp-risk" },
    { label: "料金", target: "sp-price" },
    { label: "Q&A", target: "sp-faq" },
  ];

  return (
    <>
      <InjectablesSubHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      {/* ABOUT TREATMENT */}
      <Section id="sp-about" en={data.aboutEn} jp={data.aboutJp} tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            {data.aboutParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* TREATMENT AREAS */}
      <Section id="sp-areas" en={data.areasEn} jp={data.areasJp} lead={data.areasLead} tone="taupe">
        <AreaGroups groups={data.areaGroups} />
      </Section>

      {/* RECOMMENDED FOR */}
      <Section id="sp-recommended" en={data.recommendedEn} jp={data.recommendedJp} tone="plain">
        <ConcernList items={data.recommended} />
      </Section>

      {/* FEATURES */}
      <Section id="sp-features" en={data.featuresEn} jp={data.featuresJp} tone="taupe">
        <FeatureList items={data.features} />
      </Section>

      {/* METHOD */}
      <Section id="sp-method" en="METHOD" jp="施術情報" tone="plain">
        <Reveal>
          <MethodTable items={data.method} />
        </Reveal>
      </Section>

      {/* TREATMENT FLOW */}
      <Section id="sp-flow" en="TREATMENT FLOW" jp="施術の流れ" tone="taupe">
        <FlowSteps items={data.flow} />
      </Section>

      {/* DOWNTIME */}
      {/* DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {downtimeRows.length > 0 ? (
        <Section id="sp-downtime" en={data.downtimeEn} jp={data.downtimeJp} tone="plain">
          <DowntimeTable items={downtimeRows} />
        </Section>
      ) : null}

      {/* RISK / PRECAUTIONS */}
      <Section id="sp-risk" en={data.riskEn} jp={data.riskJp} tone="taupe">
        <Reveal>
          <div className="max-w-3xl border-l-2 border-accent-500 pl-5 md:pl-6">
            <div className="space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
              {data.riskParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* PRICE */}
      <Section id="sp-price" en="PRICE" jp="料金" tone="plain">
        <div className="max-w-3xl">
          <div className="grid grid-cols-[1.6fr_1fr] border-t border-foreground-950/20 pb-3 pt-4 text-[11px] tracking-[0.15em] text-foreground-500">
            <span>対象</span>
            <span className="text-right">料金</span>
          </div>
          {data.price.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[1.6fr_1fr] items-baseline border-t border-foreground-950/12 py-5"
            >
              <span className="pr-6 font-heading text-[15px] text-foreground-950">{row.label}</span>
              <span className="text-right font-label text-[15px] text-foreground-900">{row.price}</span>
            </div>
          ))}
          <p className="mt-4 text-[12px] leading-[2] text-foreground-500">表示価格はすべて税込です。</p>
        </div>
        <div className="mt-8">
          <TreatmentPriceLink categoryId={data.priceTab} categoryJp="注入治療" label="注入治療の料金を見る" />
        </div>
        {/* ボトックス モニターLP導線（医療脱毛と同様、「料金表を見る」の直後に配置。モニター価格は公式サイトに掲載しない） */}
        {/* 今回のLPはボトックス対象のため、ヒアルロン酸ページには表示しない。 */}
        {data.slug === "botox" ? (
          <div className="mt-6 md:mt-8">
            <FirstVisitMonitorLink
              title="初めての方限定・ボトックスモニターのご案内"
              description="初めてご来院いただく方向けのボトックスモニターの内容・条件は、専用ページでご案内しています。"
              label="ボトックスモニターを見る"
              url="https://sbscl.biz/botox/#reservation"
            />
          </div>
        ) : null}
      </Section>

      {/* Q&A */}
      <Section id="sp-faq" en="Q&amp;A" jp="よくあるご質問" tone="taupe">
        <div className="max-w-3xl">
          <TreatmentFaq items={data.faqs} />
        </div>
        <div className="mt-12">
          <Link
            to="/treatment/injectables"
            className="group inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-foreground-800 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
            注入治療へ戻る
          </Link>
        </div>
      </Section>

      {/* RELATED LINKS */}
      <RelatedLinks items={data.related} />

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-primary-700 py-20 md:py-24 lg:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div className="u-container relative">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
              <span className="en-eyebrow text-[10px] text-accent-300 md:text-[11px]">INJECTABLES</span>
              <h2 className="mt-7 font-heading text-[24px] leading-[1.7] text-background-50 md:text-[34px]">
                {data.ctaHeading}
              </h2>
              <p className="mt-7 max-w-xl text-[13px] leading-[2.2] text-background-200 md:text-sm">
                気になる部位やご希望をお伺いし、医師の診察のもとで治療をご提案します。
              </p>

              <div className="mt-10 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
                <LineButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-8 py-3.5 text-[14px] font-medium text-primary-700 transition-colors hover:bg-secondary-100">
                  <i className="ri-line-fill text-base" aria-hidden />
                  {data.ctaLineLabel}
                </LineButton>
                <ReserveButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-8 py-3.5 text-[14px] text-background-50 transition-colors hover:bg-background-50/10">
                  <i className="ri-calendar-check-line text-base" aria-hidden />
                  無料カウンセリングを予約
                </ReserveButton>
              </div>

              <p className="mt-8 text-[12px] tracking-wide text-background-200/80">
                {clinic.hours} ／ 渋谷駅近く
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}